/**
 * Vue Router configuration
 */
import { createRouter, createWebHistory } from 'vue-router'
import { ROUTES } from '../../core/config/constants.js'
import { authGuard, roleGuard } from './guards.js'

// Views
import HomeView from '../../ui/views/HomeView.vue'
import LoginView from '../../ui/views/LoginView.vue'
import JoinNetworkView from '../../ui/views/JoinNetworkView.vue'
import PolicyView from '../../ui/views/PolicyView.vue'
import PrivacyView from '../../ui/views/PrivacyView.vue'
import SignageTermsView from '../../ui/views/SignageTermsView.vue'
import AccessibilityView from '../../ui/views/AccessibilityView.vue'
import AppShellLayout from '../../ui/layouts/AppShellLayout.vue'
import CatalogView from '../../ui/views/CatalogView.vue'
import ImmersiveCatalogView from '../../ui/views/ImmersiveCatalogView.vue'
import AdminDashboardView from '../../ui/views/admin/AdminDashboardView.vue'
import AdminCatalogView from '../../ui/views/admin/AdminCatalogView.vue'
import AdminDataView from '../../ui/views/admin/AdminDataView.vue'
import AdminUsersView from '../../ui/views/admin/AdminUsersView.vue'
import AdminClientsView from '../../ui/views/admin/AdminClientsView.vue'
import AdminDepartmentsView from '../../ui/views/admin/AdminDepartmentsView.vue'
import AdminPromotionsView from '../../ui/views/admin/AdminPromotionsView.vue'
import AdminLevelsView from '../../ui/views/admin/AdminLevelsView.vue'
import AdminCommitsView from '../../ui/views/admin/AdminCommitsView.vue'
import ProfileView from '../../ui/views/profile/ProfileView.vue'
import AgentsPortalView from '../../ui/views/agents/AgentsPortalView.vue'
import AgentClientsView from '../../ui/views/agents/AgentClientsView.vue'
import AgentClientDetailsView from '../../ui/views/agents/AgentClientDetailsView.vue'
import AgentCatalogView from '../../ui/views/agents/AgentCatalogView.vue'
import CreateNewClientView from '../../ui/views/agents/CreateNewClientView.vue'
import UpdateClientDetailsView from '../../ui/views/agents/UpdateClientDetailsView.vue'
import GoalsView from '../../ui/views/goals/GoalsView.vue'
import GviaView from '../../ui/views/gvia/GviaView.vue'
import GviaSuccessPage from '../../ui/views/gvia/GviaSuccessPage.vue'
import HolidaysView from '../../ui/views/holidays/HolidaysView.vue'
import StatisticsView from '../../ui/views/statistics/StatisticsView.vue'
import CheckoutView from '../../ui/views/checkout/CheckoutView.vue'
import MyOrdersView from '../../ui/views/orders/MyOrdersView.vue'
import NotFoundView from '../../ui/views/NotFoundView.vue'

const routes = [
  {
    path: '/',
    name: ROUTES.HOME,
    component: HomeView,
    meta: { requiresAuth: false },
  },
  // Redesign preview of the public home page — standalone, remove once approved
  {
    path: '/homev2',
    name: ROUTES.HOME_V2,
    component: () => import('@ui/views/homev2/HomeV2View.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/login',
    name: ROUTES.LOGIN,
    component: LoginView,
    meta: { requiresAuth: false, guestOnly: true },
  },
  {
    path: '/join',
    name: ROUTES.JOIN_NETWORK,
    component: JoinNetworkView,
    meta: { requiresAuth: false },
  },
  {
    path: '/policy',
    name: ROUTES.POLICY,
    component: PolicyView,
    meta: { requiresAuth: false },
  },
  {
    path: '/privacy',
    name: ROUTES.PRIVACY,
    component: PrivacyView,
    meta: { requiresAuth: false },
  },
  {
    // Safety net only. The real unsubscribe page is the static public/unsubscribe/index.html,
    // served by Hosting before the SPA. A returning visitor whose OLD service worker
    // (pre-denylist) hands that URL to the SPA lands here instead of on a 404.
    path: '/unsubscribe',
    name: ROUTES.UNSUBSCRIBE,
    component: () => import('../../ui/views/UnsubscribeFallbackView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/signage-terms',
    name: ROUTES.SIGNAGE_TERMS,
    component: SignageTermsView,
    meta: { requiresAuth: false },
  },
  {
    path: '/accessibility',
    name: ROUTES.ACCESSIBILITY,
    component: AccessibilityView,
    meta: { requiresAuth: false },
  },
  {
    path: '/contact',
    name: ROUTES.CONTACT,
    component: () => import('@ui/views/ContactView.vue'),
    meta: { requiresAuth: false },
  },
  // Full-screen "story" showcase — a cinematic slide experience presenting
  // every capability of the platform. Public and standalone (no app shell).
  // Temporarily disabled — uncomment to publish the /story experience.
  // {
  //   path: '/story',
  //   name: ROUTES.STORY,
  //   component: () => import('../../ui/views/story/StoryView.vue'),
  //   meta: { requiresAuth: false },
  // },
  {
    path: '/app',
    name: ROUTES.APP,
    component: AppShellLayout,
    meta: { requiresAuth: true },
    redirect: { name: ROUTES.CATALOG },
    children: [
      {
        // ── Visitor Mode ────────────────────────────────────────
        // allowGuest: true lets unauthenticated users reach this route.
        // The catalog store detects !isAuthenticated and enters 'visitor'
        // mode: loads assortment 10001 items only (no prices/promos/discounts).
        // See catalog.js store for full visitor flow documentation.
        // ────────────────────────────────────────────────────────
        path: 'catalog',
        name: ROUTES.CATALOG,
        component: CatalogView,
        meta: { allowGuest: true },
      },
      {
        // Immersive ("אימרסיבי") full-screen catalog. Shares the catalog store
        // bootstrap with the normal catalog; allowGuest mirrors it so a direct
        // visitor degrades gracefully (browse only, no prices/cart).
        path: 'catalog/immersive',
        name: ROUTES.CATALOG_IMMERSIVE,
        component: ImmersiveCatalogView,
        meta: { allowGuest: true },
      },
      {
        path: 'catalog/not-in-assortment',
        name: ROUTES.NOT_IN_ASSORTMENT,
        component: () => import('../../ui/views/NotInAssortmentView.vue'),
      },
      {
        path: 'catalog/assortment-request',
        name: ROUTES.ASSORTMENT_REQUEST,
        component: () => import('../../ui/views/AssortmentRequestView.vue'),
      },
      {
        path: 'catalog/promotion/:promoId',
        name: ROUTES.PROMOTION_DETAIL,
        component: () => import('../../ui/views/PromotionDetailView.vue'),
      },
      {
        path: 'quantity-discount/:paritMerakez',
        name: ROUTES.QUANTITY_DISCOUNT,
        component: () => import('../../ui/views/QuantityDiscountView.vue'),
      },
      {
        path: 'profile',
        name: ROUTES.PROFILE,
        component: ProfileView,
      },
      {
        path: 'admin',
        name: ROUTES.ADMIN_DASHBOARD,
        component: AdminDashboardView,
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/catalog',
        name: ROUTES.ADMIN_CATALOG,
        component: AdminCatalogView,
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/data',
        name: ROUTES.ADMIN_DATA,
        component: AdminDataView,
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/users',
        name: ROUTES.ADMIN_USERS,
        component: AdminUsersView,
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/clients',
        name: ROUTES.ADMIN_CLIENTS,
        component: AdminClientsView,
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/departments',
        name: ROUTES.ADMIN_DEPARTMENTS,
        component: AdminDepartmentsView,
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/departments/:deptId/subs/:subId/items',
        name: ROUTES.ADMIN_SUB_ITEMS,
        component: () => import('../../ui/views/admin/AdminSubItemsView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/promotions',
        name: ROUTES.ADMIN_PROMOTIONS,
        component: AdminPromotionsView,
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/whatsapp',
        name: ROUTES.ADMIN_WHATSAPP,
        component: () => import('../../ui/views/admin/AdminWhatsappView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/levels',
        name: ROUTES.ADMIN_LEVELS,
        component: AdminLevelsView,
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/commits',
        name: ROUTES.ADMIN_COMMITS,
        component: AdminCommitsView,
        beforeEnter: roleGuard(['admin']),
        meta: { title: 'ניהול קומיטים' },
      },
      {
        path: 'admin/emails',
        name: ROUTES.ADMIN_EMAILS,
        component: () => import('../../ui/views/admin/AdminEmailsView.vue'),
        beforeEnter: roleGuard(['admin']),
        meta: { title: 'ניהול אימיילים' },
      },
      {
        path: 'admin/mailing',
        name: ROUTES.ADMIN_MAILING,
        component: () => import('../../ui/views/admin/AdminMailingView.vue'),
        beforeEnter: roleGuard(['admin']),
        meta: { title: 'ניהול מערכת דיוור' },
      },
      {
        path: 'admin/orders',
        name: ROUTES.ADMIN_ORDERS,
        component: () => import('../../ui/views/admin/AdminOrdersView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/orders/:clientMakat/:orderId',
        name: ROUTES.ADMIN_ORDER_DETAILS,
        component: () => import('../../ui/views/admin/AdminOrderDetailsView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/contact-forms',
        name: ROUTES.ADMIN_CONTACT_FORMS,
        component: () => import('../../ui/views/admin/AdminContactFormsView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/b2b-forms',
        name: ROUTES.ADMIN_B2B_FORMS,
        component: () => import('../../ui/views/admin/AdminB2BFormsView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/b2b-forms/:formId',
        name: ROUTES.ADMIN_B2B_FORM_DETAILS,
        component: () => import('../../ui/views/admin/AdminB2BFormDetailsView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/b2b-divur',
        name: ROUTES.ADMIN_B2B_DIVUR,
        component: () => import('../../ui/views/admin/AdminB2BDivurView.vue'),
        beforeEnter: roleGuard(['admin']),
        meta: { title: 'דיוור' },
      },
      {
        path: 'admin/campaign-management',
        name: ROUTES.ADMIN_CAMPAIGN_MANAGEMENT,
        component: () => import('../../ui/views/admin/AdminCampaignManagementView.vue'),
        beforeEnter: roleGuard(['admin']),
        meta: { title: 'ניהול קמפיינים' },
      },
      {
        path: 'admin/agent-requests',
        name: ROUTES.ADMIN_AGENT_REQUESTS,
        component: () => import('../../ui/views/admin/AdminAgentRequestsView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/sales-analysis',
        name: ROUTES.ADMIN_SALES_ANALYSIS,
        component: () => import('../../ui/views/admin/sales-analysis/SalesAnalysisView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/sales-analysis/agents-sales',
        name: ROUTES.ADMIN_AGENTS_SALES,
        component: () => import('../../ui/views/admin/sales-analysis/AgentsSalesView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/sales-analysis/agent-orders/:agentCode',
        name: ROUTES.ADMIN_AGENT_ORDERS,
        component: () => import('../../ui/views/admin/sales-analysis/AgentOrdersView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/sales-analysis/agent-route/:agentCode',
        name: ROUTES.ADMIN_AGENT_ROUTE_TODAY,
        component: () => import('../../ui/views/admin/sales-analysis/AgentRouteTodayView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/attendance',
        name: ROUTES.ADMIN_ATTENDANCE,
        component: () => import('../../ui/views/admin/AdminAttendanceView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        path: 'admin/salary-wizard',
        name: ROUTES.ADMIN_SALARY_WIZARD,
        component: () => import('../../ui/views/admin/salary-wizard/AdminSalaryWizardView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      {
        // Dev Blog — monthly product changelog for management. Content lives in
        // versioned JSON files and is regenerated by the `/devblog` agent command.
        path: 'devblog',
        name: ROUTES.ADMIN_DEVBLOG,
        component: () => import('../../ui/views/admin/devblog/DevBlogView.vue'),
        beforeEnter: roleGuard(['admin']),
        meta: { title: 'יומן הפיתוח' },
      },
      {
        // Dev Requests — internal development requests submitted by admins
        // from the Dev Blog (subject, description, image attachments).
        path: 'devblog/requests',
        name: ROUTES.ADMIN_DEV_REQUESTS,
        component: () => import('../../ui/views/admin/devblog/DevRequestsView.vue'),
        beforeEnter: roleGuard(['admin']),
        meta: { title: 'בקשות פיתוח' },
      },
      // Agents routes
      {
        path: 'agents/portal',
        name: ROUTES.AGENTS_PORTAL,
        component: AgentsPortalView,
        // Note: roleGuard will be added later once portal content exists
      },
      {
        path: 'agents/clients',
        name: ROUTES.AGENTS_CLIENTS,
        component: AgentClientsView,
      },
      {
        path: 'agents/clients/:clientId',
        name: ROUTES.AGENTS_CLIENT_DETAILS,
        component: AgentClientDetailsView,
      },
      {
        path: 'agents/catalog/:clientMakat',
        name: ROUTES.AGENTS_CATALOG,
        component: AgentCatalogView,
      },
      {
        // Immersive catalog in the agent/admin on-behalf flow. Same view as the
        // client immersive; the clientMakat param + agentOverride query carry
        // the agent context (the view reuses the agent catalog's bootstrapped
        // store and bounces back to AGENTS_CATALOG when that context is missing).
        path: 'agents/catalog/:clientMakat/immersive',
        name: ROUTES.AGENTS_CATALOG_IMMERSIVE,
        component: ImmersiveCatalogView,
      },
      {
        path: 'agents/catalog/:clientMakat/not-in-assortment',
        name: ROUTES.AGENTS_NOT_IN_ASSORTMENT,
        component: () => import('../../ui/views/NotInAssortmentView.vue'),
      },
      {
        path: 'agents/catalog/:clientMakat/assortment-request',
        name: ROUTES.AGENTS_ASSORTMENT_REQUEST,
        component: () => import('../../ui/views/AssortmentRequestView.vue'),
      },
      {
        path: 'agents/catalog/:clientMakat/promotion/:promoId',
        name: ROUTES.AGENTS_PROMOTION_DETAIL,
        component: () => import('../../ui/views/PromotionDetailView.vue'),
      },
      {
        path: 'agents/quantity-discount/:clientMakat/:paritMerakez',
        name: ROUTES.AGENTS_QUANTITY_DISCOUNT,
        component: () => import('../../ui/views/QuantityDiscountView.vue'),
      },
      {
        path: 'agents/route-planning',
        name: ROUTES.AGENTS_ROUTE_PLANNING,
        component: () => import('../../ui/views/admin/sales-analysis/AgentRouteTodayView.vue'),
        beforeEnter: roleGuard(['agent', 'admin']),
      },
      {
        path: 'agents/clients/new',
        name: ROUTES.AGENTS_CREATE_CLIENT,
        component: CreateNewClientView,
      },
      {
        path: 'agents/clients/update',
        name: ROUTES.AGENTS_UPDATE_CLIENT,
        component: UpdateClientDetailsView,
      },
      {
        path: 'agents/goals',
        name: ROUTES.AGENTS_GOALS,
        component: GoalsView,
      },
      {
        path: 'agents/gvia',
        name: ROUTES.AGENTS_GVIA,
        component: GviaView,
        beforeEnter: roleGuard(['agent', 'admin']),
      },
      {
        path: 'agents/gvia/success',
        name: ROUTES.AGENTS_GVIA_SUCCESS,
        component: GviaSuccessPage,
        beforeEnter: roleGuard(['agent', 'admin']),
      },
      {
        path: 'agents/holidays',
        name: ROUTES.AGENTS_HOLIDAYS,
        component: HolidaysView,
        beforeEnter: roleGuard(['agent', 'admin']),
      },
      {
        path: 'agents/statistics',
        name: ROUTES.AGENTS_STATISTICS,
        component: StatisticsView,
        beforeEnter: roleGuard(['agent', 'admin']),
      },
      {
        path: 'agents/requests',
        name: ROUTES.AGENTS_REQUESTS,
        component: () => import('../../ui/views/agents/AgentRequestsView.vue'),
        beforeEnter: roleGuard(['agent', 'admin']),
      },
      {
        path: 'agents/app-transfer',
        name: ROUTES.AGENTS_APP_TRANSFER,
        component: () => import('../../ui/views/agents/AppTransferView.vue'),
        beforeEnter: roleGuard(['agent', 'admin']),
      },
      {
        path: 'agents/clients/:clientId/payment-terms-request',
        name: ROUTES.AGENTS_PAYMENT_TERMS_REQUEST,
        component: () => import('../../ui/views/agents/PaymentTermsRequestView.vue'),
        beforeEnter: roleGuard(['agent', 'admin']),
      },
      {
        path: 'agents/attendance',
        name: ROUTES.AGENTS_ATTENDANCE,
        component: () => import('../../ui/views/agents/AgentAttendanceView.vue'),
        beforeEnter: roleGuard(['agent', 'admin']),
      },
      {
        path: 'agents/wallet',
        name: ROUTES.AGENTS_WALLET,
        component: () => import('../../ui/views/agents/AgentWalletView.vue'),
        beforeEnter: roleGuard(['agent', 'admin']),
      },
      {
        path: 'agents/wallet/packages',
        name: ROUTES.AGENTS_WALLET_PACKAGES,
        component: () => import('../../ui/views/agents/AdminAllPackagesView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
      // Checkout route
      {
        path: 'checkout',
        name: ROUTES.CHECKOUT,
        component: CheckoutView,
      },
      // Payment success route
      {
        path: 'payment/success',
        name: ROUTES.PAYMENT_SUCCESS,
        component: () => import('../../ui/views/checkout/PaymentSuccessView.vue'),
      },
      // My Orders route (for non-agent users)
      {
        path: 'orders',
        name: ROUTES.MY_ORDERS,
        component: MyOrdersView,
      },
      // ── Signage Module ──
      {
        path: 'signage',
        name: ROUTES.SIGNAGE_DASHBOARD,
        component: () => import('@features/client-modules/signage/views/SignageDashboardView.vue'),
        beforeEnter: roleGuard(['client', 'admin']),
      },
      {
        path: 'signage/editor',
        name: ROUTES.SIGNAGE_EDITOR_NEW,
        component: () => import('@features/client-modules/signage/views/SignageEditorView.vue'),
        beforeEnter: roleGuard(['client', 'admin']),
      },
      {
        path: 'signage/editor/:signId',
        name: ROUTES.SIGNAGE_EDITOR_EDIT,
        component: () => import('@features/client-modules/signage/views/SignageEditorView.vue'),
        beforeEnter: roleGuard(['client', 'admin']),
      },
      {
        path: 'signage/saved',
        name: ROUTES.SIGNAGE_SAVED,
        component: () => import('@features/client-modules/signage/views/SignageSavedView.vue'),
        beforeEnter: roleGuard(['client', 'admin']),
      },
      {
        path: 'signage/settings',
        name: ROUTES.SIGNAGE_SETTINGS,
        component: () => import('@features/client-modules/signage/views/SignageSettingsView.vue'),
        beforeEnter: roleGuard(['client', 'admin']),
      },
      {
        path: 'admin/client-apps/signage',
        name: ROUTES.ADMIN_SIGNAGE_SELECT,
        component: () => import('../../ui/views/admin/AdminSignageSelectView.vue'),
        beforeEnter: roleGuard(['admin']),
      },
    ],
  },
  // Convenience alias: /devblog → /app/devblog (admin-guarded inside the shell)
  {
    path: '/devblog',
    redirect: { name: ROUTES.ADMIN_DEVBLOG },
  },
  // Catch-all 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundView,
    meta: { requiresAuth: false },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Apply auth guard
router.beforeEach(authGuard)

export default router
